import { useEffect, useState } from 'react'
import Icon from './Icon'
import { cacheShowCoordinates, geocodeVenue, locateVenue } from '../lib/geocode'
import Map from './Map'
import type { Coordinates } from '../lib/geocode'

/**
 * The venue on a map.
 *
 * Coordinates come from the show document when they're already cached, and are
 * geocoded once and written back when they aren't — so the map is instant for
 * everyone after the first person opens the show.
 *
 * Rendering is delegated to `Map`, which composes Carto basemap tiles itself.
 * The OpenStreetMap iframe this used to be brought OSM's own chrome and its
 * loudest default style along with it.
 */
export default function VenueMap({
  showId,
  query,
  parts,
  lat,
  lon,
  label,
}: {
  showId: string
  /** Full address string, used for the "open in maps" link. */
  query: string
  /** The venue's pieces, so a failed lookup can fall back to the city. */
  parts?: {
    venueName?: string | null
    venueAddress?: string | null
    city?: string | null
    country?: string | null
  }
  lat?: number | null
  lon?: number | null
  label: string
}) {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(
    typeof lat === 'number' && typeof lon === 'number' ? { lat, lon } : null,
  )
  const [state, setState] = useState<'idle' | 'looking' | 'failed'>(
    typeof lat === 'number' && typeof lon === 'number' ? 'idle' : 'looking',
  )

  useEffect(() => {
    if (typeof lat === 'number' && typeof lon === 'number') {
      setCoordinates({ lat, lon })
      setState('idle')
      return
    }
    if (!query.trim()) {
      setState('failed')
      return
    }

    let alive = true
    setState('looking')
    // The chain, not the single combined string: an imported show has no
    // cached coordinates, and "The Jam Factory, 159 17th Avenue, Tauranga
    // South, Tauranga 3112 (Historic Village), Tauranga, New Zealand" matches
    // nothing at all.
    const lookup = parts ? locateVenue(parts) : geocodeVenue(query)
    void lookup.then((found) => {
      if (!alive) return
      if (!found) {
        setState('failed')
        return
      }
      setCoordinates(found)
      setState('idle')
      void cacheShowCoordinates(showId, found)
    })
    return () => {
      alive = false
    }
  }, [showId, query, lat, lon])

  if (state === 'looking') {
    return (
      <Frame>
        <div className="stack" style={{ alignItems: 'center', gap: 8 }}>
          <div
            className="spin"
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: '2px solid var(--hairline)',
              borderTopColor: 'var(--accent)',
            }}
          />
          <span className="t-caption">Finding the venue…</span>
        </div>
      </Frame>
    )
  }

  if (state === 'failed' || !coordinates) {
    return (
      <Frame>
        <div className="stack" style={{ alignItems: 'center', gap: 6, padding: 16 }}>
          <Icon name="mapPin" size={20} style={{ color: 'var(--text-tertiary)' }} />
          <span className="t-caption" style={{ textAlign: 'center' }}>
            Couldn't place this address on a map. Check the venue address, or open it in Maps.
          </span>
        </div>
      </Frame>
    )
  }

  return <Map lat={coordinates.lat} lon={coordinates.lon} label={label} />
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        height: 240,
        borderRadius: 'var(--radius-card)',
        border: 'var(--hairline-width) solid var(--hairline)',
        background: 'var(--panel-raised)',
      }}
    >
      {children}
    </div>
  )
}
